// src/use-rect.ts
import { useCallback, useEffect, useRef } from "react";
function useRect(dispatchChange, { resize = false } = {}) {
  const dispatchChangeRef = useRef(dispatchChange);
  useEffect(() => {
    dispatchChangeRef.current = dispatchChange;
  }, [dispatchChange]);
  const resizeRef = useRef(resize);
  useEffect(() => {
    resizeRef.current = resize;
    setupResizeObserver();
  }, [resize]);
  const elementRef = useRef(null);
  const setElement = useCallback((element) => {
    if (element === elementRef.current) {
      return;
    }
    elementRef.current = element;
    revalidate();
    setupResizeObserver();
  }, []);
  const rectRef = useRef(null);
  const revalidate = useCallback(
    ({ force = false } = {}) => {
      if (!elementRef.current) {
        return;
      }
      const nextRect = elementRef.current.getBoundingClientRect();
      if (force || shouldDispatchRectChange(rectRef.current, nextRect)) {
        rectRef.current = nextRect;
        const { bottom, height, left, right, top, width, x, y } = nextRect;
        dispatchChangeRef.current({
          bottom,
          height,
          left,
          right,
          top,
          width,
          x,
          y
        });
      }
    },
    []
  );
  const resizeObserverRef = useRef(null);
  const setupResizeObserver = useCallback(() => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    if (elementRef.current && resizeRef.current) {
      resizeObserverRef.current = new ResizeObserver(() => revalidate());
      resizeObserverRef.current.observe(elementRef.current);
    }
  }, [revalidate]);
  useEffect(revalidate);
  return [setElement, revalidate];
}
var RECT_KEYS = [
  "bottom",
  "height",
  "left",
  "right",
  "top",
  "width",
  "x",
  "y"
];
function shouldDispatchRectChange(rect, nextRect) {
  return !rect || rect !== nextRect && RECT_KEYS.some((key) => rect[key] !== nextRect[key]);
}

// src/use-window-on.ts
import { useEffect as useEffect2, useRef as useRef2 } from "react";
function useWindowOn(eventType, callback) {
  const callbackRef = useRef2(callback);
  useEffect2(() => {
    callbackRef.current = callback;
  });
  useEffect2(() => {
    if (typeof window === "undefined") {
      return void 0;
    }
    const listener = (event) => {
      callbackRef.current(event);
    };
    const options = {
      capture: true,
      passive: true
    };
    window.addEventListener(eventType, listener, options);
    return () => {
      window.removeEventListener(eventType, listener, options);
    };
  }, [eventType]);
}
export {
  useRect,
  useWindowOn
};
